import React from 'react';
import { Auth0Provider as Auth0ProviderBase } from '@auth0/auth0-react';

export interface AuthProviderProps {
  /**
   * Auth0 domain
   */
  domain: string;
  /**
   * Auth0 client ID
   */
  clientId: string;
  /**
   * Authorization parameters including redirect_uri, audience, scope
   */
  authorizationParams: {
    redirect_uri: string;
    audience?: string;
    scope: string;
  };
  /**
   * Cache location - 'localstorage' or 'memory'
   */
  cacheLocation?: 'localstorage' | 'memory';
  /**
   * Enable refresh tokens
   */
  useRefreshTokens?: boolean;
  /**
   * Callback after redirect from Auth0
   */
  onRedirectCallback?: (appState?: { returnTo?: string }) => void;
  /**
   * Children components
   */
  children: React.ReactNode;
}

/**
 * AuthProvider wrapper that configures Auth0
 *
 * @example
 * ```tsx
 * import { AuthProvider } from '@lucid/auth';
 * import { auth0Config } from './config/auth0';
 *
 * function App() {
 *   return (
 *     <AuthProvider {...auth0Config}>
 *       <YourApp />
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  domain,
  clientId,
  authorizationParams,
  cacheLocation = 'localstorage',
  useRefreshTokens = true,
  onRedirectCallback,
  children,
}) => {
  const defaultOnRedirectCallback = (appState?: { returnTo?: string }) => {
    window.history.replaceState({}, document.title, appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0ProviderBase
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      cacheLocation={cacheLocation}
      useRefreshTokens={useRefreshTokens}
      onRedirectCallback={onRedirectCallback || defaultOnRedirectCallback}
    >
      {children}
    </Auth0ProviderBase>
  );
};
